import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart, faUser, faPhone, faMapMarkerAlt, faCalendarAlt, faFileInvoice, faBox, faRupeeSign, faTag,
    faBalanceScale, faDollarSign, faCalculator, faExclamationTriangle, faPlus, faMinus, faTrash, faSave, faTimes, faTrashAlt,
    faSpinner, faCheckCircle, faExclamationCircle, faSearch, faBarcode, faQrcode, faCreditCard, faMoneyBill,
    faUniversity, faWallet, faPercent, faReceipt, faChevronDown, faChevronUp, faBoxes, faHistory, faUndo, faRedo,
    faPrint, faFilePdf, faDownload, faUpload, faEdit, faChartLine, faTimesCircle, faShoppingBag, faAddressCard,
    faIdCard, faKey, faLock, faUnlock, faEye, faEyeSlash, faStore, faEnvelope, faGlobe, faBuilding, faFileAlt,
    faTruck, faWeightHanging, faCubes, faLayerGroup, faToggleOn, faToggleOff, faCopy, faList, faGift, faPercent as faPercentIcon,
    faInfoCircle, faCoins
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// API Endpoints
// ==============================================
const API_BASE = 'https://ourzestapp.online/oz_backend/pharmacy_module';
const SELL_API = `${API_BASE}/pharmacy_sell.php`;
const CUSTOMER_API = `${API_BASE}/pharmacy_customers.php`;
const PRODUCT_API = `${API_BASE}/pharmacy_products.php`;

// ==============================================
// Helper function to parse BQuantity
// ==============================================
const parseBQuantityNumeric = (bquantityStr) => {
    if (!bquantityStr) return 0;
    const match = bquantityStr.match(/^([\d,\.]+)/);
    if (match) {
        return parseFloat(match[1].replace(/,/g, ''));
    }
    return 0;
};

// ==============================================
// Calculate Effective Sell Price with Discounts
// ==============================================
const calculateEffectiveSellPrice = (mrp, tradePercent, extraPercent, bonusBuy, bonusFree) => {
    if (!mrp || parseFloat(mrp) <= 0) {
        return {
            effectiveSellPrice: 0,
            discountPercent: 0,
            totalQuantity: 1,
            perUnitPrice: 0,
            bonusPerUnit: 0,
            basePrice: 0
        };
    }

    let price = parseFloat(mrp);
    const tp = parseFloat(tradePercent) || 0;
    const extra = parseFloat(extraPercent) || 0;
    const buy = parseInt(bonusBuy) || 0;
    const free = parseInt(bonusFree) || 0;

    if (tp > 0) {
        price = price * (1 - tp / 100);
    }

    if (extra > 0) {
        price = price * (1 - extra / 100);
    }

    let totalQuantity = buy > 0 ? buy : 1;
    let bonusPerUnit = 0;
    let effectiveSellPrice = price;
    let perUnitPrice = price;

    if (buy > 0 && free > 0) {
        bonusPerUnit = parseFloat(mrp) / buy;
        effectiveSellPrice = price - bonusPerUnit;
        perUnitPrice = effectiveSellPrice / buy;
        totalQuantity = buy + free;
    } else if (buy > 0 && free == 0) {
        perUnitPrice = price / buy;
        effectiveSellPrice = price;
        totalQuantity = buy;
    } else {
        perUnitPrice = price;
        effectiveSellPrice = price;
        totalQuantity = 1;
    }

    let discountPercent = 0;
    if (parseFloat(mrp) > 0 && effectiveSellPrice > 0 && effectiveSellPrice < parseFloat(mrp)) {
        discountPercent = (1 - effectiveSellPrice / parseFloat(mrp)) * 100;
    }

    return {
        effectiveSellPrice: Math.round(effectiveSellPrice * 100) / 100,
        discountPercent: Math.round(discountPercent * 100) / 100,
        totalQuantity: totalQuantity,
        perUnitPrice: Math.round(perUnitPrice * 100) / 100,
        bonusPerUnit: Math.round(bonusPerUnit * 100) / 100,
        basePrice: Math.round(price * 100) / 100
    };
};

// ==============================================
// Translation Utility
// ==============================================
const createSellTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_sell_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Sell Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_sell_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'New Sale': 'نیا سیل',
                'Edit Sale': 'سیل میں ترمیم کریں',
                'Sale Details': 'سیل کی تفصیلات',
                'Sell Products': 'پروڈکٹس بیچیں',
                'Bill Number': 'بل نمبر',
                'Date': 'تاریخ',
                'Customer Details': 'کسٹمر کی تفصیلات',
                'Customer Name': 'کسٹمر کا نام',
                'Phone Number': 'فون نمبر',
                'Address': 'پتہ',
                'Search Products': 'پروڈکٹس تلاش کریں',
                'Product Name': 'پروڈکٹ',
                'Product Code': 'پروڈکٹ کوڈ',
                'Batch': 'بیچ',
                'Price': 'قیمت',
                'Quantity': 'مقدار',
                'Total': 'کل',
                'Remove': 'ہٹائیں',
                'Stock': 'اسٹاک',
                'Subtotal': 'ذیلی کل',
                'Tax': 'ٹیکس',
                'Discount': 'ڈسکاؤنٹ',
                'Grand Total': 'کل رقم',
                'Paid Amount': 'ادا کردہ رقم',
                'Balance': 'باقی رقم',
                'Payment Method': 'ادائیگی کا طریقہ',
                'Cash': 'نقد',
                'Card': 'کارڈ',
                'UPI': 'یوپی آئی',
                'Complete Sale': 'سیل مکمل کریں',
                'Update Sale': 'سیل اپ ڈیٹ کریں',
                'Clear': 'صاف کریں',
                'Save Draft': 'ڈرافٹ محفوظ کریں',
                'Drafts': 'ڈرافٹس',
                'Saved Drafts': 'محفوظ کردہ ڈرافٹس',
                'No drafts saved': 'کوئی ڈرافٹ محفوظ نہیں',
                'Draft loaded': 'ڈرافٹ لوڈ ہو گیا',
                'Draft cleared': 'ڈرافٹ صاف ہو گیا',
                'Clear Form': 'فارم صاف کریں',
                'View Drafts': 'ڈرافٹس دیکھیں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'receipt': 'رسید',
                'printReceipt': 'رسید پرنٹ کریں',
                'Scanner Ready': 'اسکینر تیار ہے',
                'Product Found': 'پروڈکٹ مل گیا',
                'Product Not Found': 'پروڈکٹ نہیں ملا',
                'MRP': 'ایم آر پی',
                'Trade Percent': 'ٹریڈ فیصد',
                'Extra Percent': 'اضافی فیصد',
                'Bonus': 'بونس',
                'Buy': 'خریدیں',
                'Free': 'مفت',
                'Effective Price': 'مؤثر قیمت',
                'Discount Percent': 'ڈسکاؤنٹ فیصد',
                'Cost Price': 'لاگت قیمت',
                'Profit': 'منافع'
            },
            'ar': {
                'New Sale': 'بيع جديد',
                'Edit Sale': 'تعديل البيع',
                'Sale Details': 'تفاصيل البيع',
                'Sell Products': 'بيع المنتجات',
                'Bill Number': 'رقم الفاتورة',
                'Date': 'التاريخ',
                'Customer Details': 'تفاصيل العميل',
                'Customer Name': 'اسم العميل',
                'Phone Number': 'رقم الهاتف',
                'Address': 'العنوان',
                'Search Products': 'البحث عن منتجات',
                'Product Name': 'المنتج',
                'Product Code': 'رمز المنتج',
                'Batch': 'الدفعة',
                'Price': 'السعر',
                'Quantity': 'الكمية',
                'Total': 'المجموع',
                'Remove': 'إزالة',
                'Stock': 'المخزون',
                'Subtotal': 'المجموع الفرعي',
                'Tax': 'الضريبة',
                'Discount': 'الخصم',
                'Grand Total': 'المجموع الكلي',
                'Paid Amount': 'المبلغ المدفوع',
                'Balance': 'الرصيد',
                'Payment Method': 'طريقة الدفع',
                'Cash': 'نقدي',
                'Card': 'بطاقة',
                'UPI': 'يو بي آي',
                'Complete Sale': 'إتمام البيع',
                'Update Sale': 'تحديث البيع',
                'Clear': 'مسح',
                'Save Draft': 'حفظ المسودة',
                'Drafts': 'المسودات',
                'Saved Drafts': 'المسودات المحفوظة',
                'No drafts saved': 'لا توجد مسودات محفوظة',
                'Draft loaded': 'تم تحميل المسودة',
                'Draft cleared': 'تم مسح المسودة',
                'Clear Form': 'مسح النموذج',
                'View Drafts': 'عرض المسودات',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'receipt': 'إيصال',
                'printReceipt': 'طباعة الإيصال',
                'Scanner Ready': 'الماسح جاهز',
                'Product Found': 'تم العثور على المنتج',
                'Product Not Found': 'المنتج غير موجود',
                'MRP': 'السعر الأقصى',
                'Trade Percent': 'نسبة التجارة',
                'Extra Percent': 'نسبة إضافية',
                'Bonus': 'مكافأة',
                'Buy': 'شراء',
                'Free': 'مجاني',
                'Effective Price': 'السعر الفعال',
                'Discount Percent': 'نسبة الخصم',
                'Cost Price': 'سعر التكلفة',
                'Profit': 'الربح'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        newSale: 'New Sale',
        editSale: 'Edit Sale',
        saleDetails: 'Sale Details',
        sellProducts: 'Sell Products',
        billNumber: 'Bill Number',
        date: 'Date',
        customerDetails: 'Customer Details',
        customerName: 'Customer Name',
        customerPhone: 'Phone Number',
        customerAddress: 'Address',
        searchProducts: 'Search Products',
        productName: 'Product',
        productCode: 'Product Code',
        batch: 'Batch',
        price: 'Price',
        quantity: 'Qty',
        total: 'Total',
        remove: 'Remove',
        stock: 'Stock',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        grandTotal: 'Grand Total',
        paidAmount: 'Paid Amount',
        balance: 'Balance',
        paymentMethod: 'Payment Method',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        completeSale: 'Save & Close',
        updateSale: 'Update Sale',
        clear: 'Clear',
        saving: 'Saving...',
        loading: 'Loading...',
        saleCompleted: 'Sale completed successfully!',
        saleUpdated: 'Sale updated successfully!',
        failedSave: 'Failed to save sale',
        failedUpdate: 'Failed to update sale',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected',
        itemsRequired: 'At least one item is required',
        quantityExceedsStock: 'Quantity exceeds available stock',
        invalidQuantity: 'Please enter a valid quantity',
        invalidPrice: 'Please enter a valid price',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        saveDraft: 'Save Draft',
        drafts: 'Drafts',
        savedDrafts: 'Saved Drafts',
        noDraftsSaved: 'No drafts saved',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        clearForm: 'Clear Form',
        viewDrafts: 'View Drafts',
        goBack: 'Go Back',
        sellByPack: 'Sell by Pack',
        sellByUnit: 'Sell by Unit',
        receipt: 'Receipt',
        printReceipt: 'Print Receipt',
        thankYou: 'Thank you for your purchase!',
        poweredBy: 'Powered by OurZest',
        scannerReady: 'Scanner Ready',
        productFound: 'Product found!',
        productNotFound: 'Product not found',
        MRP: 'MRP',
        tradePercent: 'Trade %',
        extraPercent: 'Extra %',
        bonus: 'Bonus',
        buy: 'Buy',
        free: 'Free',
        effectivePrice: 'Effective Price',
        discountPercent: 'Discount %',
        costPrice: 'Cost Price',
        profit: 'Profit'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const sellTranslator = createSellTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Helper function to format stock display dynamically
// ==============================================
// ==============================================
// Helper function to format stock display dynamically
// ==============================================
// ==============================================
// Helper function to format stock display dynamically
// ==============================================
const formatStockDisplay = (item) => {
    const availablePacks = parseFloat(item.available_pack_stock) || 0;
    const itemsPerPack = parseFloat(item.items_per_pack) || 1;
    const packContentQuantity = parseFloat(item.pack_content_quantity) || 0;
    const packBaseUnitLabel = item.pack_base_unit_label || 'Unit';
    const packingType = item.packing_type || 'Pack';
    const subPackingType = item.sub_packing_type || 'Strip';
    const unitorstrip = item.unitorstrip || ''; // Get unitorstrip from item
    
    // For base unit sell method (tablets/capsules)
    if (item.sellMethod === 'base_unit') {
        const totalBaseUnits = availablePacks * packContentQuantity;
        const unitLabel = packBaseUnitLabel || item.unit_of_measure || 'Unit';
        const displayLabel = unitLabel.charAt(0).toUpperCase() + unitLabel.slice(1);
        return `${totalBaseUnits.toFixed(0)} ${displayLabel}`;
    }
    // For sub-packing sell method
    else if (item.sellMethod === 'sub_packing') {
        const totalSubPacks = availablePacks * itemsPerPack;
        let displayLabel = '';
        
        // Use unitorstrip to determine the display label
        if (unitorstrip === 'strip') {
            // For strip products, show "Strip" or the strip size
            const stripMatch = subPackingType.match(/box_(\d+)x(\d+)/i);
            if (stripMatch) {
                const strips = stripMatch[1];
                const perStrip = stripMatch[2];
                displayLabel = `Strips`;
            } else {
                displayLabel = 'Strips';
            }
        } else if (unitorstrip === 'unit') {
            // For unit products, show the sub_packing_type without "box_" prefix
            const cleanLabel = subPackingType.replace(/^box_/, '').replace(/_/g, ' ');
            displayLabel = cleanLabel.charAt(0).toUpperCase() + cleanLabel.slice(1);
        } else {
            // Default: try to clean up the sub_packing_type
            const cleanLabel = subPackingType.replace(/^box_/, '').replace(/_/g, ' ');
            displayLabel = cleanLabel.charAt(0).toUpperCase() + cleanLabel.slice(1) || 'Unit';
        }
        
        return `${totalSubPacks.toFixed(0)} ${displayLabel}`;
    }
    // For packing sell method (boxes)
    else {
        // Use packing_type if available, otherwise use 'Pack'
        const displayLabel = packingType || 'Pack';
        return `${availablePacks.toFixed(0)} ${displayLabel}`;
    }
};

// ==============================================
// Build the valid sell-method options for a product,
// based on its packaging hierarchy AND unitorstrip field
// ==============================================
// ==============================================
// Build the valid sell-method options for a product,
// based on its packaging hierarchy AND unitorstrip field
// ==============================================
// ==============================================
// Build the valid sell-method options for a product,
// based on its packaging hierarchy AND unitorstrip field
// ==============================================
const getSellMethodOptions = (product) => {
    const options = [];
    
    const packingType = (product?.packing_type || '').trim().toLowerCase();
    const subPackingType = (product?.sub_packing_type || '').trim();
    const unitOfMeasure = (product?.unit_of_measure || '').trim();
    const unitorstrip = (product?.unitorstrip || '').trim();
    const itemsPerPack = parseFloat(product?.items_per_pack) || 1;
    const packContentQuantity = parseFloat(product?.pack_content_quantity) || 0;
    const packBaseUnitLabel = (product?.pack_base_unit_label || '').trim();
    const purchaseType = (product?.purchase_type || 'packing').trim().toLowerCase();

    const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

    const isUnitBased = packingType === 'unit' || packingType === 'piece' || purchaseType === 'unit';
    
    if (isUnitBased) {
        // Agar unitorstrip === 'unit' hai to koi option hi mat do
        if (unitorstrip === 'unit') {
            return options; // empty array — no dropdown row
        }
        
        let label = unitorstrip || subPackingType || unitOfMeasure || 'Piece';
        label = label.replace(/^box_/, '').replace(/_/g, ' ');
        label = capitalize(label);
        
        options.push({ 
            value: 'packing',
            label: label 
        });
        return options;
    }

    // TIER 1: PACK
    options.push({
        value: 'packing',
        label: capitalize(packingType || 'Pack')
    });

    // TIER 2: SUB-PACK — sirf tab push karo jab unitorstrip 'unit' NA ho
    if (subPackingType && itemsPerPack > 1 && unitorstrip !== 'unit') {
        let subLabel = '';
        
        if (unitorstrip === 'strip') {
            subLabel = 'Strip';
        } else if (unitorstrip) {
            subLabel = capitalize(unitorstrip.replace(/^box_/, '').replace(/_/g, ' '));
        } else {
            subLabel = capitalize(subPackingType);
        }
        
        options.push({
            value: 'sub_packing',
            label: subLabel
        });
    }

    // TIER 3: BASE UNIT
    if (packBaseUnitLabel && packContentQuantity > 0) {
        options.push({
            value: 'base_unit',
            label: capitalize(packBaseUnitLabel)
        });
    }

    return options;
};

// ==============================================
// Sell Method Dropdown Component
// ==============================================
const SellMethodDropdown = ({ value, onChange, disabled, product }) => {
    const options = getSellMethodOptions(product);

    // Koi valid sell method option nahi hai — dropdown mat dikhao
    if (options.length === 0) {
        return <span className="ozui-no-sell-method">—</span>;
    }

    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="ozui-sell-method-select"
        >
            {options.map(opt => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
    );
};

// ==============================================
// Helper function to calculate item total
// ==============================================
const calculateItemTotal = (price, quantity) => {
    const p = parseFloat(price) || 0;
    const q = parseFloat(quantity) || 0;
    return (p * q).toFixed(2);
};

// ==============================================
// Items Table Component
// ==============================================
const ItemsTable = ({
    items,
    onItemChange,
    onRemove,
    onAdd,
    onSellMethodChange,
    onDiscountChange,
    errors,
    disabled,
    translations,
    isRTL
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Desktop view with all fields
    if (!isMobile) {
        return (
            <div className="ozui-items-table-container">
                <div className="ozui-table-header-actions">
                    <h4><FontAwesomeIcon icon={faBoxes} />{translations?.itemsSold || 'Items'}</h4>
                    <button type="button" className="ozui-btn-add-item" onClick={onAdd} disabled={disabled}>
                        <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                    </button>
                </div>
                <div className="ozui-table-wrapper">
                    <table className="ozui-items-table">
                        <thead>
                            <tr>
                                <th style={{ minWidth: '80px' }}>{translations?.productName || 'Product'}</th>
                                <th style={{ minWidth: '60px' }}>Batch</th>
                                <th style={{ minWidth: '100px' }}>{translations?.sellUnit || 'Sell Method'}</th>
                                <th style={{ minWidth: '70px' }}>MRP</th>
                                <th style={{ minWidth: '50px' }}>Trade %</th>
                                <th style={{ minWidth: '50px' }}>Extra %</th>
                                <th style={{ minWidth: '60px' }}>Bonus (B/F)</th>
                                <th style={{ minWidth: '80px' }}>Eff. Price</th>
                                <th style={{ minWidth: '50px' }}>Qty</th>
                                <th style={{ minWidth: '80px' }}>Stock</th>
                                <th style={{ minWidth: '60px' }}>Total</th>
                                <th style={{ width: '30px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => {
                                const productForDropdown = {
                                    packing_type: item.packing_type,
                                    sub_packing_type: item.sub_packing_type,
                                    unit_of_measure: item.unit_of_measure,
                                    unitorstrip: item.unitorstrip, // Pass unitorstrip to dropdown
                                    items_per_pack: item.items_per_pack,
                                    pack_content_label: item.pack_content_label,
                                    pack_base_unit_label: item.pack_base_unit_label,
                                    pack_content_quantity: item.pack_content_quantity,
                                    purchase_type: item.purchase_type
                                };

                                return (
                                    <tr key={index} className={`${errors[`quantity_${index}`] ? 'ozui-error-row' : ''}`}>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input type="text" value={item.product_name} readOnly className="ozui-readonly-input" />
                                                {item.product_code && <span style={{ fontSize: '0.6rem', color: '#6b7280' }}>{item.product_code}</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input type="text" value={item.batch_no || ''} readOnly className="ozui-readonly-input" />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <SellMethodDropdown
                                                    value={item.sellMethod || 'packing'}
                                                    onChange={(val) => onSellMethodChange(index, val)}
                                                    disabled={disabled}
                                                    product={productForDropdown}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input type="text" value={item.mrp || ''} readOnly className="ozui-readonly-input" style={{ fontWeight: 'bold', color: '#1a56db' }} />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input 
                                                    type="number" 
                                                    value={item.trade_percent || ''} 
                                                    onChange={(e) => onDiscountChange(index, 'trade_percent', e.target.value)}
                                                    step="0.01" 
                                                    min="0" 
                                                    max="100" 
                                                    placeholder="0" 
                                                    disabled={disabled} 
                                                    style={{ width: '50px' }}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input 
                                                    type="number" 
                                                    value={item.extra_percent || ''} 
                                                    onChange={(e) => onDiscountChange(index, 'extra_percent', e.target.value)}
                                                    step="0.01" 
                                                    min="0" 
                                                    max="100" 
                                                    placeholder="0" 
                                                    disabled={disabled} 
                                                    style={{ width: '50px' }}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field" style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                <input 
                                                    type="number" 
                                                    value={item.bonus_buy || ''} 
                                                    onChange={(e) => onDiscountChange(index, 'bonus_buy', e.target.value)}
                                                    step="1" 
                                                    min="0" 
                                                    placeholder="Buy" 
                                                    disabled={disabled} 
                                                    style={{ width: '40px' }}
                                                />
                                                <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>/</span>
                                                <input 
                                                    type="number" 
                                                    value={item.bonus_free || ''} 
                                                    onChange={(e) => onDiscountChange(index, 'bonus_free', e.target.value)}
                                                    step="1" 
                                                    min="0" 
                                                    placeholder="Free" 
                                                    disabled={disabled} 
                                                    style={{ width: '40px' }}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input 
                                                    type="text" 
                                                    value={item.effective_sell_price || ''} 
                                                    readOnly 
                                                    className="ozui-readonly-input" 
                                                    style={{ fontWeight: 'bold', color: '#059669', width: '80px' }} 
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input 
                                                    type="number" 
                                                    value={item.quantity} 
                                                    onChange={(e) => onItemChange(index, 'quantity', parseFloat(e.target.value))} 
                                                    step="0.001" 
                                                    min="0.001" 
                                                    max={item.available_stock} 
                                                    placeholder="0" 
                                                    disabled={disabled} 
                                                    dir="ltr" 
                                                    className={errors[`quantity_${index}`] ? 'ozui-error' : ''} 
                                                    style={{ width: '60px' }}
                                                />
                                                {errors[`quantity_${index}`] && <div className="ozui-table-error">{errors[`quantity_${index}`]}</div>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <span className={`ozui-stock-badge ${parseFloat(item.available_stock || 0) <= (item.quantity_alert || 5) ? 'ozui-low-stock' : ''}`}>
                                                    {formatStockDisplay(item)}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ozui-table-field">
                                                <input type="text" value={calculateItemTotal(item.effective_sell_price || item.price, item.quantity)} readOnly disabled className="ozui-readonly-input ozui-total-display" style={{ width: '70px' }} />
                                            </div>
                                        </td>
                                        <td>
                                            <button type="button" className="ozui-table-remove-btn" onClick={() => onRemove(index)} disabled={disabled || items.length === 1} title={translations?.remove || 'Remove'}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Mobile view
    return (
        <div className="ozui-items-mobile-container">
            <div className="ozui-items-header-mobile">
                <h4><FontAwesomeIcon icon={faBoxes} />{translations?.itemsSold || 'Items'}</h4>
                <button type="button" className="ozui-btn-add-item-mobile" onClick={onAdd} disabled={disabled}>
                    <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="ozui-items-cards-container">
                {items.map((item, index) => {
                    const isPackingMode = item.sellMethod === 'packing';
                    const productForDropdown = {
                        packing_type: item.packing_type,
                        sub_packing_type: item.sub_packing_type,
                        unit_of_measure: item.unit_of_measure,
                        unitorstrip: item.unitorstrip, // Pass unitorstrip to dropdown
                        items_per_pack: item.items_per_pack,
                        pack_content_label: item.pack_content_label,
                        pack_base_unit_label: item.pack_base_unit_label,
                        pack_content_quantity: item.pack_content_quantity,
                        purchase_type: item.purchase_type
                    };

                    return (
                        <div key={index} className={`ozui-item-card-mobile ${isPackingMode ? 'ozui-packing-card' : 'ozui-subpacking-card'}`}>
                            <div className="ozui-item-card-header">
                                <span className="ozui-item-number">Item {index + 1}</span>
                                <div className="ozui-sell-method-toggle-mobile">
                                    <span className={`ozui-method-badge ${isPackingMode ? 'ozui-packing' : 'ozui-subpacking'}`}>
                                        {isPackingMode ? (item.packing_type || 'Pack') : (item.sub_packing_type || 'Unit')}
                                    </span>
                                </div>
                                <button type="button" className="ozui-item-remove-btn-mobile" onClick={() => onRemove(index)} disabled={disabled || items.length === 1}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                            <div className="ozui-item-card-content">
                                <div className="ozui-mobile-field">
                                    <label>{translations?.productName || 'Product'}</label>
                                    <input type="text" value={item.product_name} readOnly className="ozui-readonly-input" />
                                </div>
                                <div className="ozui-mobile-field">
                                    <label>Batch</label>
                                    <input type="text" value={item.batch_no || ''} readOnly className="ozui-readonly-input" />
                                </div>
                                <div className="ozui-mobile-field">
                                    <label>{translations?.sellUnit || 'Sell Method'}</label>
                                    <SellMethodDropdown
                                        value={item.sellMethod || 'packing'}
                                        onChange={(val) => onSellMethodChange(index, val)}
                                        disabled={disabled}
                                        product={productForDropdown}
                                    />
                                </div>
                                <div className="ozui-mobile-field">
                                    <label>MRP</label>
                                    <input type="text" value={item.mrp || ''} readOnly className="ozui-readonly-input" style={{ fontWeight: 'bold', color: '#1a56db' }} />
                                </div>
                                <div className="ozui-mobile-row">
                                    <div className="ozui-mobile-field half">
                                        <label>Trade %</label>
                                        <input 
                                            type="number" 
                                            value={item.trade_percent || ''} 
                                            onChange={(e) => onDiscountChange(index, 'trade_percent', e.target.value)}
                                            step="0.01" 
                                            min="0" 
                                            max="100" 
                                            placeholder="0" 
                                            disabled={disabled} 
                                        />
                                    </div>
                                    <div className="ozui-mobile-field half">
                                        <label>Extra %</label>
                                        <input 
                                            type="number" 
                                            value={item.extra_percent || ''} 
                                            onChange={(e) => onDiscountChange(index, 'extra_percent', e.target.value)}
                                            step="0.01" 
                                            min="0" 
                                            max="100" 
                                            placeholder="0" 
                                            disabled={disabled} 
                                        />
                                    </div>
                                </div>
                                <div className="ozui-mobile-row">
                                    <div className="ozui-mobile-field half">
                                        <label>Bonus Buy</label>
                                        <input 
                                            type="number" 
                                            value={item.bonus_buy || ''} 
                                            onChange={(e) => onDiscountChange(index, 'bonus_buy', e.target.value)}
                                            step="1" 
                                            min="0" 
                                            placeholder="0" 
                                            disabled={disabled} 
                                        />
                                    </div>
                                    <div className="ozui-mobile-field half">
                                        <label>Bonus Free</label>
                                        <input 
                                            type="number" 
                                            value={item.bonus_free || ''} 
                                            onChange={(e) => onDiscountChange(index, 'bonus_free', e.target.value)}
                                            step="1" 
                                            min="0" 
                                            placeholder="0" 
                                            disabled={disabled} 
                                        />
                                    </div>
                                </div>
                                <div className="ozui-mobile-field">
                                    <label>Eff. Price</label>
                                    <input type="text" value={item.effective_sell_price || ''} readOnly className="ozui-readonly-input" style={{ fontWeight: 'bold', color: '#059669' }} />
                                </div>
                                <div className={`ozui-mobile-field half ${errors[`quantity_${index}`] ? 'ozui-has-error' : ''}`}>
                                    <label>{translations?.quantity || 'Qty'} *</label>
                                    <input 
                                        type="number" 
                                        value={item.quantity} 
                                        onChange={(e) => onItemChange(index, 'quantity', parseFloat(e.target.value))} 
                                        step="0.001" 
                                        min="0.001" 
                                        max={item.available_stock} 
                                        placeholder="0" 
                                        disabled={disabled} 
                                        dir="ltr" 
                                    />
                                </div>
                                <div className="ozui-mobile-field">
                                    <label>Stock</label>
                                    <input 
                                        type="text" 
                                        value={formatStockDisplay(item)} 
                                        readOnly 
                                        className="ozui-readonly-input" 
                                    />
                                </div>
                                <div className="ozui-mobile-field">
                                    <label>{translations?.total || 'Total'}</label>
                                    <input type="text" value={calculateItemTotal(item.effective_sell_price || item.price, item.quantity)} readOnly disabled className="ozui-readonly-input ozui-total-display" />
                                </div>
                                {errors[`quantity_${index}`] && <div className="ozui-error-text">{errors[`quantity_${index}`]}</div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ==============================================
// Main Sell Component
// ==============================================
const PharmacySell = ({ saleId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const initialFormData = {
        date: new Date().toISOString().split('T')[0],
        bill_number: '',
        bill_number_int: 0,
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        customer_id: null,
        subtotal: '0.00',
        tax: '0.00',
        discount: '0.00',
        grand_total: '0.00',
        paid_amount: '0.00',
        balance: '0.00',
        payment_method: 'cash',
        payment_status: 'unpaid',
        notes: '',
        items: [],
        packing_status: 'unpacked'
    };

    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [currentThemeColor, setCurrentThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });

    // Draft State
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);

    // Customer Search
    const [customerSearchResults, setCustomerSearchResults] = useState([]);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [searchingCustomer, setSearchingCustomer] = useState(false);

    // PRODUCT SEARCH - CLIENT-SIDE
    const [allProducts, setAllProducts] = useState([]);
    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(false);

    // Barcode Scanner State
    const [isScannerActive, setIsScannerActive] = useState(true);
    const [lastScannedBarcode, setLastScannedBarcode] = useState('');
    const barcodeInputRef = useRef(null);
    const scanCooldownRef = useRef(false);
    const scanTimerRef = useRef(null);

    // Business Info for Receipt
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        businessaddress: '',
        phonenumber: '',
        email: '',
        bussinesslogo: '',
        bussinesslogo_url: ''
    });

    // Auto-focus ref for Customer Name field
    const customerNameRef = useRef(null);

    // Refs
    const toastTimerRef = useRef(null);
    const customerSearchRef = useRef(null);
    const productSearchRef = useRef(null);
    const isSavingRef = useRef(false);
    const submitButtonRef = useRef(null);
    const draftsDropdownRef = useRef(null);

    // ==============================================
    // LOAD ALL PRODUCTS FOR CLIENT-SIDE SEARCH
    // ==============================================
    const loadAllProducts = useCallback(async () => {
        if (!user?.username || !currentShop?.shop_id) {
            console.log('❌ Cannot load products: Missing user or shop');
            return;
        }
        
        setLoadingProducts(true);
        try {
            console.log('🔍 Loading all products for sell search...');
            
            const response = await fetch(PRODUCT_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    action: 'get_records', 
                    username: user.username, 
                    shop_id: currentShop.shop_id, 
                    limit: 1000 
                })
            });
            
            const data = await response.json();
            console.log('📦 Sell products response:', data);
            
            if (data.status === 'success') {
                const mapped = (data.data || []).map(p => ({
                    id: p.id,
                    product_name: p.product_name || '',
                    batch_no: p.batch_no || '',
                    product_code: p.product_code || '',
                    barcode: p.barcode || '',
                    category: p.category || '',
                    company: p.company || '',
                    color: p.color || '',
                    size: p.size || '',
                    expiry_date: p.expiry_date || '',
                    purchase_type: p.purchase_type || 'packing',
                    packing_type: p.packing_type || '',
                    sub_packing_type: p.sub_packing_type || '',
                    unitorstrip: p.unitorstrip || '', // NEW: Load unitorstrip
                    items_per_pack: parseFloat(p.items_per_pack) || 1,
                    unit_of_measure: p.unit_of_measure || 'piece',
                    sell_per_pack: parseFloat(p.sell_per_pack) || 0,
                    total_quantity: parseFloat(p.total_quantity) || 0,
                    quantity_alert: parseFloat(p.quantity_alert) || 5,
                    mrp: parseFloat(p.mrp) || 0,
                    trade_percent: parseFloat(p.trade_percent) || 0,
                    extra_percent: parseFloat(p.extra_percent) || 0,
                    bonus_buy: parseInt(p.bonus_buy) || 0,
                    bonus_free: parseInt(p.bonus_free) || 0,
                    effective_sell_price: parseFloat(p.effective_cost_price) || parseFloat(p.sell_per_pack) || 0,
                    effective_discount_percent: parseFloat(p.effective_discount_percent) || 0,
                    bonus_stock_quantity: parseFloat(p.bonus_stock_quantity) || 0,
                    actual_stock_quantity: parseFloat(p.actual_stock_quantity) || 0,
                    bquantity: p.bquantity || '',
                    available_bulk: parseBQuantityNumeric(p.bquantity || '0'),
                    available_pack_stock: parseFloat(p.total_quantity) || 0,
                    available_unit_stock: parseBQuantityNumeric(p.bquantity || '0'),
                    sell_price_per_unit: parseFloat(p.sell_price_per_item_unit) || 0,
                    cost_price_per_pack: parseFloat(p.effective_cost_price) || 0,
                    cost_price_per_unit: parseFloat(p.cost_price_per_base_unit) || 0,
                    pack_content_quantity: parseFloat(p.pack_content_quantity) || 0,
                    pack_content_label: p.pack_content_label || '',
                    pack_base_unit_label: p.pack_base_unit_label || '',
                    mrp_per_pack_unit: parseFloat(p.mrp_per_pack_unit) || 0,
                    mrp_per_base_unit: parseFloat(p.mrp_per_base_unit) || 0
                }));
                
                setAllProducts(mapped);
                console.log('✅ Sell products loaded:', mapped.length);
            } else {
                console.warn('⚠️ Failed to load sell products:', data.message);
                setAllProducts([]);
            }
        } catch (error) {
            console.error('❌ Error loading sell products:', error);
            setAllProducts([]);
        } finally {
            setLoadingProducts(false);
        }
    }, [user?.username, currentShop?.shop_id]);

    // Load products on mount and shop change
    useEffect(() => {
        loadAllProducts();
    }, [loadAllProducts]);

    // ==============================================
    // CLIENT-SIDE PRODUCT SEARCH
    // ==============================================
    const handleProductSearch = useCallback((term) => {
        console.log('🔍 Sell search for term:', term, 'in', allProducts.length, 'products');
        
        setProductSearchTerm(term || '');
        
        if (!term || term.trim().length < 1) {
            setFilteredProducts([]);
            setShowProductDropdown(false);
            return;
        }
        
        const searchLower = term.toLowerCase().trim();
        
        const filtered = allProducts.filter(p => {
            const name = (p.product_name || '').toLowerCase();
            const batch = (p.batch_no || '').toLowerCase();
            const code = (p.product_code || '').toLowerCase();
            const barcode = (p.barcode || '').toLowerCase();
            const company = (p.company || '').toLowerCase();
            const category = (p.category || '').toLowerCase();
            
            return name.includes(searchLower) || 
                   batch.includes(searchLower) || 
                   code.includes(searchLower) || 
                   barcode.includes(searchLower) ||
                   company.includes(searchLower) ||
                   category.includes(searchLower);
        });
        
        console.log('🔍 Sell filtered results:', filtered.length);
        setFilteredProducts(filtered);
        setShowProductDropdown(filtered.length > 0);
    }, [allProducts]);

    // ==============================================
    // CLIENT-SIDE BARCODE SCANNING
    // ==============================================
    const processBarcodeScanClientSide = useCallback(async (barcode) => {
        if (scanCooldownRef.current) return;
        scanCooldownRef.current = true;
        setLastScannedBarcode(barcode);

        if (!allProducts || allProducts.length === 0) {
            showToast('error', 'Products not loaded yet. Please try again.');
            scanCooldownRef.current = false;
            return;
        }

        try {
            const searchLower = barcode.toLowerCase().trim();
            const exactMatch = allProducts.find(p => 
                (p.product_code && p.product_code.toLowerCase() === searchLower) ||
                (p.barcode && p.barcode.toLowerCase() === searchLower)
            );
            
            const partialMatches = allProducts.filter(p => 
                (p.product_code && p.product_code.toLowerCase().includes(searchLower)) ||
                (p.barcode && p.barcode.toLowerCase().includes(searchLower)) ||
                (p.product_name && p.product_name.toLowerCase().includes(searchLower))
            );
            
            const rawProduct = exactMatch || (partialMatches.length > 0 ? partialMatches[0] : null);

            if (rawProduct) {
                const processedProduct = {
                    id: rawProduct.id,
                    product_name: rawProduct.product_name,
                    product_code: rawProduct.product_code || '',
                    batch_no: rawProduct.batch_no || '',
                    barcode: rawProduct.barcode || '',
                    purchase_type: rawProduct.purchase_type || 'packing',
                    sell_price: parseFloat(rawProduct.sell_per_pack) || parseFloat(rawProduct.mrp) || 0,
                    sell_per_pack: parseFloat(rawProduct.sell_per_pack) || 0,
                    sell_price_per_unit: parseFloat(rawProduct.sell_price_per_unit) || 0,
                    quantity: parseFloat(rawProduct.total_quantity) || 0,
                    bquantity: rawProduct.bquantity || '',
                    available_bulk: parseBQuantityNumeric(rawProduct.bquantity || '0'),
                    available_pack_stock: parseFloat(rawProduct.total_quantity) || 0,
                    available_unit_stock: parseBQuantityNumeric(rawProduct.bquantity || '0'),
                    quantity_alert: parseFloat(rawProduct.quantity_alert) || 5,
                    category: rawProduct.category || '',
                    company: rawProduct.company || '',
                    color: rawProduct.color || '',
                    size: rawProduct.size || '',
                    expiry_date: rawProduct.expiry_date || '',
                    packing_type: rawProduct.packing_type || '',
                    sub_packing_type: rawProduct.sub_packing_type || '',
                    unitorstrip: rawProduct.unitorstrip || '', // NEW: Load unitorstrip
                    items_per_pack: parseFloat(rawProduct.items_per_pack) || 1,
                    unit_of_measure: rawProduct.unit_of_measure || 'piece',
                    mrp: rawProduct.mrp || 0,
                    trade_percent: 0,
                    extra_percent: 0,
                    bonus_buy: 0,
                    bonus_free: 0,
                    effective_sell_price: parseFloat(rawProduct.mrp) || parseFloat(rawProduct.sell_per_pack) || 0,
                    effective_discount_percent: 0,
                    bonus_per_unit: 0,
                    cost_price_per_pack: parseFloat(rawProduct.cost_price_per_pack) || 0,
                    cost_price_per_unit: parseFloat(rawProduct.cost_price_per_unit) || 0,
                    bonus_stock_quantity: parseFloat(rawProduct.bonus_stock_quantity) || 0,
                    actual_stock_quantity: parseFloat(rawProduct.actual_stock_quantity) || 0,
                    pack_content_label: rawProduct.pack_content_label || '',
                    pack_content_quantity: parseFloat(rawProduct.pack_content_quantity) || 0,
                    pack_base_unit_label: rawProduct.pack_base_unit_label || '',
                    mrp_per_pack_unit: parseFloat(rawProduct.mrp_per_pack_unit) || 0,
                    mrp_per_base_unit: parseFloat(rawProduct.mrp_per_base_unit) || 0
                };

                handleProductSelect(processedProduct);
                showToast('success', `${rawProduct.product_name} - ${translations?.productFound || 'Product found!'}`);
            } else {
                showToast('error', `${translations?.productNotFound || 'Product not found'}: ${barcode}`);
            }
        } catch (error) {
            console.error('Barcode search error:', error);
            showToast('error', 'Error searching product');
        }

        setTimeout(() => { scanCooldownRef.current = false; }, 500);
    }, [allProducts, translations]);

    // ==============================================
    // GLOBAL BARCODE SCANNER
    // ==============================================
    useEffect(() => {
        let buffer = '';
        let lastTime = 0;

        const handler = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
                if (e.target === barcodeInputRef.current) return;
                return;
            }
            const now = Date.now();
            if (now - lastTime > 50) buffer = '';
            lastTime = now;
            if (e.key.length === 1) buffer += e.key;
            if (e.key === 'Enter' && buffer.length >= 6) {
                e.preventDefault();
                e.stopPropagation();
                const code = buffer.trim();
                buffer = '';
                if (code && isScannerActive) processBarcodeScanClientSide(code);
            }
            clearTimeout(scanTimerRef.current);
            scanTimerRef.current = setTimeout(() => { buffer = ''; }, 100);
        };

        document.addEventListener('keypress', handler, true);
        return () => {
            document.removeEventListener('keypress', handler, true);
            clearTimeout(scanTimerRef.current);
        };
    }, [isScannerActive, processBarcodeScanClientSide]);

    // Tab key handler
    useEffect(() => {
        const handleTabKey = (e) => {
            if (e.key === 'Tab') {
                setShowCustomerDropdown(false);
                setShowProductDropdown(false);
                setShowDraftsList(false);
            }
        };
        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, []);

    // Auto-focus on Customer Name
    useEffect(() => {
        setTimeout(() => {
            if (customerNameRef.current) {
                customerNameRef.current.focus();
            }
        }, 100);
    }, []);

    // ============================================
    // Draft Management
    // ============================================
    const getDraftStorageKey = () => {
        const userId = user?.user_id || user?.username || 'anonymous';
        const shopId = currentShop?.shop_id || 'no_shop';
        return `sell_draft_${userId}_${shopId}`;
    };

    const saveDraft = () => {
        const hasData = formData.items.length > 0 || formData.customer_name?.trim() || formData.customer_phone?.trim();
        if (!hasData) {
            showToast('warning', 'Cannot save empty draft');
            return;
        }

        const draftId = Date.now();
        const draft = {
            id: draftId,
            ...formData,
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id
        };

        const existingDrafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        existingDrafts.unshift(draft);
        const limitedDrafts = existingDrafts.slice(0, 20);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(limitedDrafts));

        showToast('success', 'Draft saved successfully!');
        loadDrafts();
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        const filteredDrafts = currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts;
        setSavedDrafts(filteredDrafts);
    };

    const loadDraft = (draft) => {
        setFormData({
            ...initialFormData,
            ...draft,
            date: draft.date || new Date().toISOString().split('T')[0],
            items: draft.items || []
        });
        setShowDraftsList(false);
        showToast('success', translations.draftLoaded || 'Draft loaded successfully!');
        setTimeout(() => {
            if (customerNameRef.current) {
                customerNameRef.current.focus();
            }
        }, 100);
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        const filteredDrafts = drafts.filter(d => d.id !== draftId);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(filteredDrafts));
        loadDrafts();
        showToast('success', 'Draft deleted successfully!');
    };

    const clearDraft = () => {
        setFormData(initialFormData);
        setProductSearchTerm('');
        setErrors({});
        setLastScannedBarcode('');
        showToast('success', translations.draftCleared || 'Form cleared');
        setTimeout(() => {
            if (customerNameRef.current) {
                customerNameRef.current.focus();
            }
        }, 100);
    };

    // ============================================
    // Theme, Dark Mode, Language Sync
    // ============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);

        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            r = 99;
            g = 102;
            b = 241;
        }

        let style = document.getElementById('dynamic-orb-styles-sell');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-orb-styles-sell';
            document.head.appendChild(style);
        }

        style.textContent = `
            .ozui-header-bg-animation .gradient-orb.orb-1 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-2 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-3 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-4 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-5 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
            }
        `;

        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                applyTheme(e.newValue || '#6366f1');
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
        };
    }, [applyTheme]);

    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);

            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozui-dark');
                } else {
                    container.classList.remove('ozui-dark');
                }
            }

            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };

        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozui-dark');
                    } else {
                        container.classList.remove('ozui-dark');
                    }
                }
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
        };
    }, []);

    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await sellTranslator.getTranslations(lang);
            setTranslations(trans);
        };

        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        loadTranslations(currentLanguage);

        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozui-container');

        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // Load drafts on shop change
    useEffect(() => {
        loadDrafts();
    }, [currentShop]);

    // Click outside handlers
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(event.target)) {
                setShowDraftsList(false);
            }
            if (customerSearchRef.current && !customerSearchRef.current.contains(event.target)) {
                setShowCustomerDropdown(false);
            }
            if (productSearchRef.current && !productSearchRef.current.contains(event.target)) {
                setShowProductDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    const getNextBillNumber = async () => {
        if (!currentShop?.shop_id) return;
        try {
            const response = await fetch(SELL_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_next_bill_number', shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setFormData(prev => ({ ...prev, bill_number: data.data.bill_number, bill_number_int: data.data.bill_number_int }));
            }
        } catch (error) {
            console.error('Error getting next bill number:', error);
        }
    };

    // ============================================
    // Fetch Business Info
    // ============================================
    const fetchBusinessInfo = useCallback(async () => {
        if (!user || !user.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=' + encodeURIComponent(user.username), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    businessaddress: data.businessaddress || '',
                    phonenumber: data.phonenumber || '',
                    email: data.email || '',
                    bussinesslogo: data.bussinesslogo || '',
                    bussinesslogo_url: data.bussinesslogo_url || ''
                });
            } else {
                setBusinessInfo({
                    businessname: user.business_name || user.fullname || '',
                    businessaddress: user.business_address || '',
                    phonenumber: user.phonenumber || '',
                    email: user.email || '',
                    bussinesslogo: '',
                    bussinesslogo_url: ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            setBusinessInfo({
                businessname: user.business_name || user.fullname || '',
                businessaddress: user.business_address || '',
                phonenumber: user.phonenumber || '',
                email: user.email || '',
                bussinesslogo: '',
                bussinesslogo_url: ''
            });
        }
    }, [user]);

    useEffect(() => {
        if (currentShop?.shop_id) {
            getNextBillNumber();
        }
        fetchBusinessInfo();
    }, [currentShop, fetchBusinessInfo]);

    useEffect(() => {
        if (saleId) {
            setIsEditMode(true);
            loadSaleData(saleId);
        }
    }, [saleId]);

    useEffect(() => {
        calculateTotals();
    }, [formData.items, formData.tax, formData.discount, formData.paid_amount]);

    const loadSaleData = async (id) => {
        if (!user?.username || !currentShop?.shop_id) return;
        setLoading(true);
        try {
            const response = await fetch(SELL_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id: id, username: user.username, shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const sale = data.data;
                setFormData(prev => ({
                    ...prev,
                    date: sale.date || new Date().toISOString().split('T')[0],
                    bill_number: sale.bill_number || '',
                    bill_number_int: sale.bill_number_int || 0,
                    customer_id: sale.customer_id || null,
                    customer_name: sale.customer_name || '',
                    customer_phone: sale.customer_phone || '',
                    customer_address: sale.customer_address || '',
                    subtotal: sale.subtotal || '0.00',
                    tax: sale.tax || '0.00',
                    discount: sale.discount || '0.00',
                    grand_total: sale.grand_total || '0.00',
                    paid_amount: sale.paid_amount || '0.00',
                    balance: sale.balance || '0.00',
                    payment_method: sale.payment_method || 'cash',
                    payment_status: sale.payment_status || 'unpaid',
                    notes: sale.notes || '',
                    items: sale.items || [],
                    packing_status: sale.packing_status || 'unpacked'
                }));
            } else {
                showToast('error', data.message || 'Failed to load sale data');
            }
        } catch (error) {
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const searchCustomers = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2 || !currentShop?.shop_id) {
            setCustomerSearchResults([]);
            setShowCustomerDropdown(false);
            return;
        }
        setSearchingCustomer(true);
        try {
            const response = await fetch(CUSTOMER_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_records', shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const filtered = data.data.filter(customer =>
                    customer.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    customer.phone?.toLowerCase().includes(searchTerm.toLowerCase())
                );
                setCustomerSearchResults(filtered.slice(0, 20));
                setShowCustomerDropdown(filtered.length > 0);
            } else {
                setCustomerSearchResults([]);
                setShowCustomerDropdown(false);
            }
        } catch (error) {
            console.error('Error searching customers:', error);
            setCustomerSearchResults([]);
            setShowCustomerDropdown(false);
        } finally {
            setSearchingCustomer(false);
        }
    }, [currentShop?.shop_id]);

    const selectCustomer = (customer) => {
        setFormData(prev => ({
            ...prev,
            customer_id: customer.customerid,
            customer_name: customer.customer_name,
            customer_phone: customer.phone || '',
            customer_address: customer.address || ''
        }));
        setShowCustomerDropdown(false);
        setCustomerSearchResults([]);
        showToast('success', `${translations?.customerSelected || 'Customer selected'}: ${customer.customer_name}`);

        setTimeout(() => {
            const productSearchInput = productSearchRef.current?.querySelector('input');
            if (productSearchInput) {
                productSearchInput.focus();
            }
        }, 100);
    };

    // ==============================================
    // Calculate stock based on sell method
    // ==============================================
   const calculateStockForMethod = (item, method) => {
    const itemsPerPack = parseFloat(item.items_per_pack) || 1;
    const availablePacks = parseFloat(item.available_pack_stock) || 0;
    const packContentQuantity = parseFloat(item.pack_content_quantity) || 0;
    const availableBulk = parseFloat(item.available_bulk) || 0; // Get the bulk stock
    const unitOfMeasure = item.unit_of_measure || 'piece';

    if (method === 'packing') {
        return availablePacks;
    } else if (method === 'base_unit') {
        // For base unit, use pack_content_quantity
        return packContentQuantity > 0 ? availablePacks * packContentQuantity : availablePacks;
    } else {
        // For sub_packing (unit sell method)
        // If it's a unit product (packing_type is 'unit' or 'piece'), use available_bulk
        if (item.packing_type === 'unit' || item.packing_type === 'piece' || item.purchase_type === 'unit') {
            return availableBulk; // Return the actual unit count from bquantity
        }
        // For regular pack products, calculate from packs
        return availablePacks * itemsPerPack;
    }
};

    // ============================================
    // Get price for method
    // ============================================
    const getPriceForMethod = useCallback((item, method) => {
        const mrp = parseFloat(item.mrp) || 0;
        const itemsPerPack = parseFloat(item.items_per_pack) || 1;
        const packContentQuantity = parseFloat(item.pack_content_quantity) || 0;
        const mrpPerPackUnit = parseFloat(item.mrp_per_pack_unit) || 0;
        const mrpPerBaseUnit = parseFloat(item.mrp_per_base_unit) || 0;
        const effectivePrice = parseFloat(item.effective_sell_price) || mrp;

        if (method === 'packing') {
            return mrp > 0 ? mrp : effectivePrice;
        } else if (method === 'base_unit') {
            if (packContentQuantity > 0 && mrp > 0) {
                return mrp / packContentQuantity;
            }
            if (mrpPerBaseUnit > 0) {
                return mrpPerBaseUnit;
            }
            return effectivePrice / (packContentQuantity || 1);
        } else {
            if (itemsPerPack > 0 && mrp > 0) {
                return mrp / itemsPerPack;
            }
            if (mrpPerPackUnit > 0) {
                return mrpPerPackUnit;
            }
            return effectivePrice / (itemsPerPack || 1);
        }
    }, []);

    // ============================================
    // handleSellMethodChange - WITH PRICE UPDATE
    // ============================================
    const handleSellMethodChange = (index, newMethod) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];

        const newPrice = getPriceForMethod(item, newMethod);
        const newMaxStock = calculateStockForMethod(item, newMethod);
        const matchedOption = getSellMethodOptions(item).find(o => o.value === newMethod);
        const newDisplayUnit = matchedOption?.label || item.sub_packing_type || item.unit_of_measure || 'unit';

        const newQuantity = newMaxStock > 0 ? 1 : 0;

        const updatedItem = {
            ...item,
            sellMethod: newMethod,
            price: newPrice,
            effective_sell_price: newPrice,
            available_stock: newMaxStock,
            display_unit: newDisplayUnit,
            quantity: newQuantity.toString(),
            total: (newPrice * newQuantity).toFixed(2)
        };

        updatedItems[index] = updatedItem;
        setFormData(prev => ({ ...prev, items: updatedItems }));

        if (newPrice > 0) {
            showToast('info', `Price: Rs. ${newPrice.toFixed(2)} per ${newDisplayUnit}`);
        }

        setErrors(prev => {
            const ne = { ...prev };
            if (newMaxStock === 0) {
                ne[`quantity_${index}`] = 'No stock available for this unit';
            } else {
                delete ne[`quantity_${index}`];
            }
            return ne;
        });
    };

    // ============================================
    // handleDiscountChange - Recalculate effective price
    // ============================================
    const handleDiscountChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];
        
        item[field] = value;
        
        const mrp = parseFloat(item.mrp) || 0;
        const tradePercent = parseFloat(item.trade_percent) || 0;
        const extraPercent = parseFloat(item.extra_percent) || 0;
        const bonusBuy = parseInt(item.bonus_buy) || 0;
        const bonusFree = parseInt(item.bonus_free) || 0;
        
        const result = calculateEffectiveSellPrice(mrp, tradePercent, extraPercent, bonusBuy, bonusFree);
        
        const basePrice = getPriceForMethod(item, item.sellMethod);
        const discountedPrice = basePrice - (basePrice * (result.discountPercent / 100));
        
        item.effective_sell_price = discountedPrice > 0 ? discountedPrice : result.effectiveSellPrice;
        item.effective_discount_percent = result.discountPercent;
        item.bonus_per_unit = result.bonusPerUnit;
        item.price = item.effective_sell_price;
        item.total = (item.effective_sell_price * (parseFloat(item.quantity) || 0)).toFixed(2);
        
        updatedItems[index] = item;
        setFormData(prev => ({ ...prev, items: updatedItems }));
        
        if (result.discountPercent > 0) {
            showToast('info', `Discount applied: ${result.discountPercent.toFixed(1)}% off MRP`);
        }
        
        setTimeout(() => calculateTotals(), 50);
    };

    // ============================================
    // handleProductSelect - PROPER STOCK QUANTITIES
    // ============================================
    const handleProductSelect = (product) => {
        const existingItemIndex = formData.items.findIndex(item => item.product_id === product.id);

        const availablePacks = parseFloat(product.available_pack_stock) || 0;
        const itemsPerPack = parseFloat(product.items_per_pack) || 1;
        const packContentQuantity = parseFloat(product.pack_content_quantity) || 0;

        const mrp = parseFloat(product.mrp) || 0;
        const defaultPrice = mrp > 0 ? mrp : parseFloat(product.effective_sell_price) || parseFloat(product.sell_per_pack) || 0;

        if (existingItemIndex >= 0) {
            const updatedItems = [...formData.items];
            const existingItem = updatedItems[existingItemIndex];
            const increment = 1;
            const maxQty = calculateStockForMethod(existingItem, existingItem.sellMethod);
            const newQty = parseFloat(existingItem.quantity) + increment;

            if (newQty <= maxQty || maxQty === 0) {
                updatedItems[existingItemIndex] = {
                    ...existingItem,
                    quantity: newQty,
                    total: (parseFloat(existingItem.effective_sell_price || existingItem.price) * newQty).toFixed(2)
                };
                setFormData(prev => ({ ...prev, items: updatedItems }));
                showToast('success', `Added another ${product.product_name}`);
            } else {
                showToast('error', `Cannot add more than available stock`);
            }
        } else {
            const sellMethodOptions = getSellMethodOptions(product);
            const defaultMethod = sellMethodOptions[0]?.value || 'sub_packing';
            const defaultOption = sellMethodOptions[0];

            const tempItem = {
                mrp: mrp,
                items_per_pack: itemsPerPack,
                pack_content_quantity: packContentQuantity,
                available_pack_stock: availablePacks,
                mrp_per_pack_unit: product.mrp_per_pack_unit,
                mrp_per_base_unit: product.mrp_per_base_unit,
                effective_sell_price: defaultPrice
            };
            const methodPrice = getPriceForMethod(tempItem, defaultMethod);
            const defaultStock = calculateStockForMethod(tempItem, defaultMethod);
            const defaultUnit = defaultOption?.label || product.sub_packing_type || product.unit_of_measure || 'unit';
            const defaultQuantity = defaultStock > 0 ? 1 : 0;

            const newItem = {
                product_id: product.id,
                product_name: product.product_name,
                product_code: product.product_code || '',
                batch_no: product.batch_no || '',
                barcode: product.barcode || '',
                category: product.category || '',
                company: product.company || '',
                color: product.color || '',
                size: product.size || '',
                expiry_date: product.expiry_date || '',
                purchase_type: product.purchase_type || 'packing',
                sellMethod: defaultMethod,
                display_unit: defaultUnit,
                packing_type: product.packing_type,
                sub_packing_type: product.sub_packing_type,
                unitorstrip: product.unitorstrip || '', // NEW: Store unitorstrip
                items_per_pack: itemsPerPack,
                unit_of_measure: product.unit_of_measure || 'piece',
                available_pack_stock: availablePacks,
                available_stock: defaultStock,
                price: methodPrice,
                effective_sell_price: methodPrice,
                pack_price: mrp,
                unit_price: mrp / (itemsPerPack || 1),
                quantity: defaultQuantity.toString(),
                total: (methodPrice * defaultQuantity).toFixed(2),
                quantity_alert: product.quantity_alert || 5,
                mrp: mrp,
                trade_percent: 0,
                extra_percent: 0,
                bonus_buy: 0,
                bonus_free: 0,
                effective_discount_percent: 0,
                bonus_per_unit: 0,
                cost_price_per_pack: product.cost_price_per_pack || 0,
                cost_price_per_unit: product.cost_price_per_unit || 0,
                bonus_stock_quantity: 0,
                actual_stock_quantity: product.actual_stock_quantity || 0,
                pack_content_quantity: packContentQuantity,
                pack_content_label: product.pack_content_label || '',
                pack_base_unit_label: product.pack_base_unit_label || '',
                mrp_per_pack_unit: product.mrp_per_pack_unit || 0,
                mrp_per_base_unit: product.mrp_per_base_unit || 0
            };

            setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
            showToast('success', `${product.product_name} added - ${methodPrice.toFixed(2)} per ${defaultUnit}`);
        }

        setProductSearchTerm('');
        setShowProductDropdown(false);
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];

        if (field === 'price') {
            item.price = value;
            item.effective_sell_price = value;
            item.total = (value * (parseFloat(item.quantity) || 0)).toFixed(2);
        } else if (field === 'quantity') {
            const qty = value;
            const maxQty = item.available_stock || 0;
            if (qty > maxQty && maxQty > 0) {
                setErrors(prev => ({ ...prev, [`quantity_${index}`]: translations?.quantityExceedsStock || 'Quantity exceeds available stock' }));
            } else {
                setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`quantity_${index}`]; return newErrors; });
            }
            item.quantity = qty;
            item.total = ((parseFloat(item.effective_sell_price || item.price) || 0) * qty).toFixed(2);
        }

        updatedItems[index] = item;
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const addItem = () => {
        if (productSearchRef.current) {
            const input = productSearchRef.current.querySelector('input');
            if (input) input.focus();
        }
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const updatedItems = [...formData.items];
            updatedItems.splice(index, 1);
            setFormData(prev => ({ ...prev, items: updatedItems }));
        }
    };

    const calculateTotals = () => {
        let subtotal = 0;
        formData.items.forEach(item => {
            const price = parseFloat(item.effective_sell_price || item.price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            subtotal += price * quantity;
        });
        const tax = parseFloat(formData.tax) || 0;
        const discount = parseFloat(formData.discount) || 0;
        const grandTotal = subtotal + tax - discount;
        const paid = parseFloat(formData.paid_amount) || 0;
        const balance = grandTotal - paid;
        const paymentStatus = balance <= 0 ? 'paid' : (paid > 0 ? 'partial' : 'unpaid');
        setFormData(prev => ({
            ...prev,
            subtotal: subtotal.toFixed(2),
            grand_total: grandTotal.toFixed(2),
            balance: balance.toFixed(2),
            payment_status: paymentStatus
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required';
        } else {
            formData.items.forEach((item, index) => {
                const qty = parseFloat(item.quantity) || 0;
                const maxQty = parseFloat(item.available_stock) || 0;
                if (qty > maxQty && maxQty > 0) newErrors[`quantity_${index}`] = translations?.quantityExceedsStock || 'Quantity exceeds available stock';
                if (qty <= 0) newErrors[`quantity_${index}`] = translations?.invalidQuantity || 'Please enter a valid quantity';
                const price = parseFloat(item.effective_sell_price || item.price) || 0;
                if (price <= 0) newErrors[`price_${index}`] = translations?.invalidPrice || 'Please enter a valid price';
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'customer_name') searchCustomers(value);
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handlePaidAmountChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, paid_amount: value }));
        const paid = parseFloat(value) || 0;
        const grandTotal = parseFloat(formData.grand_total) || 0;
        const balance = grandTotal - paid;
        setFormData(prev => ({
            ...prev,
            balance: balance.toFixed(2),
            payment_status: balance <= 0 ? 'paid' : (paid > 0 ? 'partial' : 'unpaid')
        }));
    };

    // ============================================
    // RECEIPT FUNCTIONS
    // ============================================
    const escapeHtml = (text) => {
        if (!text) return '';
        return String(text).replace(/[&<>]/g, function (m) {
            if (m === '&') return '&amp;';
            if (m === '<') return '&lt;';
            if (m === '>') return '&gt;';
            return m;
        });
    };

    const formatReceiptCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0.00';
        return `Rs. ${parseFloat(amount).toFixed(2)}`;
    };

    const formatReceiptDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

const handleDirectPrint = () => {
    if (formData.items.length === 0) {
        showToast('warning', 'No items to print receipt');
        return;
    }

    const printWindow = window.open('', '_blank');

    if (printWindow) {
        const shopName = businessInfo?.businessname || user?.fullname || currentShop?.shop_name || 'OurZest Store';
        const shopAddress = businessInfo?.businessaddress || '';
        const shopPhone = businessInfo?.phonenumber || '';
        const businessEmail = businessInfo?.email || '';
        const businessLogo = businessInfo?.bussinesslogo_url || businessInfo?.bussinesslogo || '';
        const customerName = formData.customer_name || 'Walk-in Customer';
        const customerPhone = formData.customer_phone || '';
        const customerAddress = formData.customer_address || '';
        const themeColor = currentThemeColor || '#6366f1';

        const getUnitLabel = (item) => {
            if (item.sellMethod === 'packing') {
                return item.packing_type || 'Box';
            } else if (item.sellMethod === 'base_unit') {
                return item.pack_base_unit_label || 'Unit';
            } else {
                return item.unitorstrip || item.sub_packing_type || 'Unit';
            }
        };

        // Check if any item has discounts applied
        const hasAnyDiscount = formData.items.some(item => 
            (parseFloat(item.trade_percent) > 0) || 
            (parseFloat(item.extra_percent) > 0) || 
            (parseInt(item.bonus_buy) > 0) || 
            (parseInt(item.bonus_free) > 0)
        );

        // Calculate total discount amount
        let totalDiscountAmount = 0;
        formData.items.forEach(item => {
            const mrp = parseFloat(item.mrp) || 0;
            const effPrice = parseFloat(item.effective_sell_price || item.price) || 0;
            const qty = parseFloat(item.quantity) || 0;
            if (mrp > 0 && effPrice > 0 && mrp > effPrice) {
                totalDiscountAmount += (mrp - effPrice) * qty;
            }
        });

        // Format discount details for an item - ONLY the breakdown, NO sum
        const getDiscountDetails = (item) => {
            const parts = [];
            if (parseFloat(item.trade_percent) > 0) parts.push(`TP ${item.trade_percent}%`);
            if (parseFloat(item.extra_percent) > 0) parts.push(`Extra ${item.extra_percent}%`);
            if (parseInt(item.bonus_buy) > 0 && parseInt(item.bonus_free) > 0) {
                parts.push(`Bonus ${item.bonus_buy}+${item.bonus_free}`);
            } else if (parseInt(item.bonus_buy) > 0) {
                parts.push(`Buy ${item.bonus_buy}`);
            } else if (parseInt(item.bonus_free) > 0) {
                parts.push(`Free ${item.bonus_free}`);
            }
            return parts.join(' | ');
        };

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Receipt ${formData.bill_number}</title>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                    /* 3-inch Thermal Printer Styles - 80mm width */
                    @page { 
                        size: 80mm auto; 
                        margin: 0; 
                    }
                    * { 
                        margin: 0; 
                        padding: 0; 
                        box-sizing: border-box; 
                    }
                    body { 
                        font-family: 'Arial', 'Helvetica Neue', sans-serif; 
                        background: #ffffff; 
                        color: #1a1a2e; 
                        font-size: 9pt; 
                        line-height: 1.4;
                        padding: 3mm 3.5mm;
                        width: 80mm;
                        margin: 0 auto;
                    }
                    .receipt {
                        max-width: 73mm;
                        margin: 0 auto;
                    }
                    .text-center { text-align: center; }
                    .text-right { text-align: right; }
                    .text-left { text-align: left; }
                    .text-uppercase { text-transform: uppercase; }
                    .font-bold { font-weight: 700; }
                    .font-small { font-size: 7.5pt; }
                    .font-large { font-size: 13pt; }
                    .font-xlarge { font-size: 15pt; }
                    
                    /* ===== HEADER ===== */
                    .header {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 12px;
                        padding: 4px 0 6px 0;
                        border-bottom: 2.5px solid #1a1a2e;
                        margin-bottom: 5px;
                    }
                    .header-logo-wrapper {
                        flex-shrink: 0;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .header-logo {
                        max-height: 70px;
                        max-width: 70px;
                        height: auto;
                        width: auto;
                        display: block;
                        object-fit: contain;
                    }
                    .header-info {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        line-height: 1.2;
                    }
                    .shop-name {
                        font-size: 15pt;
                        font-weight: 800;
                        letter-spacing: 0.8px;
                        color: #1a1a2e;
                    }
                    .shop-address {
                        font-size: 7.5pt;
                        color: #4a4a5a;
                        font-weight: 400;
                    }
                    .shop-contact {
                        font-size: 7.5pt;
                        color: #4a4a5a;
                        font-weight: 400;
                    }
                    .shop-contact .phone {
                        font-weight: 600;
                        color: #1a1a2e;
                    }
                    
                    /* ===== DIVIDERS ===== */
                    .divider {
                        border-top: 1px dashed #c0c0d0;
                        margin: 3px 0;
                    }
                    .divider-thick {
                        border-top: 2px solid #1a1a2e;
                        margin: 3px 0;
                    }
                    .divider-dotted {
                        border-top: 1px dotted #d0d0e0;
                        margin: 2px 0;
                    }
                    .divider-light {
                        border-top: 1px solid #e8e8f0;
                        margin: 2px 0;
                    }
                    
                    /* ===== BILL INFO ===== */
                    .bill-info {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        padding: 2px 0;
                        border-bottom: 1px solid #e8e8f0;
                        margin-bottom: 3px;
                    }
                    .bill-number {
                        font-size: 10pt;
                        font-weight: 700;
                        color: #1a1a2e;
                        letter-spacing: 0.5px;
                    }
                    .bill-number .label {
                        font-weight: 600;
                        color: #4a4a5a;
                    }
                    .bill-meta {
                        display: flex;
                        justify-content: center;
                        gap: 20px;
                        font-size: 8.5pt;
                        color: #4a4a5a;
                        padding: 1px 0;
                    }
                    .bill-meta .label {
                        font-weight: 600;
                        color: #1a1a2e;
                    }
                    
                    /* ===== CUSTOMER INFO ===== */
                    .customer-info {
                        text-align: center;
                        font-size: 8.5pt;
                        padding: 3px 0;
                        border-bottom: 1px solid #e8e8f0;
                        margin-bottom: 4px;
                        color: #2a2a3e;
                    }
                    .customer-info .label {
                        font-weight: 600;
                        color: #1a1a2e;
                    }
                    
                    /* ===== ITEMS TABLE ===== */
                    .items-table {
                        width: 100%;
                        border-collapse: collapse;
                        font-size: 8.5pt;
                        margin: 3px 0;
                    }
                    .items-table th {
                        text-align: left;
                        padding: 3px 1px;
                        border-bottom: 1.5px solid #1a1a2e;
                        font-size: 7.5pt;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        color: #1a1a2e;
                    }
                    .items-table td {
                        padding: 2.5px 1px;
                        vertical-align: middle;
                        border-bottom: 1px solid #f0f0f5;
                    }
                    .items-table tr:last-child td {
                        border-bottom: none;
                    }
                    .items-table .col-item {
                        width: 38%;
                    }
                    .items-table .col-qty {
                        text-align: center;
                        width: 22%;
                    }
                    .items-table .col-price {
                        text-align: right;
                        width: 18%;
                    }
                    .items-table .col-total {
                        text-align: right;
                        width: 22%;
                    }
                    .items-table .item-name {
                        font-weight: 500;
                        font-size: 8.5pt;
                        color: #1a1a2e;
                        word-break: break-word;
                    }
                    .items-table .item-unit {
                        font-size: 7pt;
                        color: #6a6a7a;
                        font-weight: 400;
                        display: inline;
                    }
                    .items-table .item-discount-detail {
                        font-size: 6.5pt;
                        color: #059669;
                        display: block;
                        margin-top: 1px;
                        font-weight: 500;
                    }
                    .items-table .qty-with-unit {
                        display: inline;
                        font-weight: 500;
                    }
                    .items-table .qty-with-unit .unit {
                        font-weight: 400;
                        color: #6a6a7a;
                        margin-left: 1px;
                    }
                    
                    /* ===== TOTALS ===== */
                    .totals {
                        margin-top: 3px;
                        padding-top: 4px;
                        border-top: 2px solid #1a1a2e;
                    }
                    .total-row {
                        display: flex;
                        justify-content: space-between;
                        padding: 1.5px 0;
                        font-size: 8.5pt;
                    }
                    .total-row .label {
                        font-weight: 600;
                        color: #1a1a2e;
                    }
                    .total-row .value {
                        font-weight: 500;
                        color: #1a1a2e;
                    }
                    .total-row.grand-total {
                        font-size: 11.5pt;
                        font-weight: 700;
                        border-top: 2px solid #1a1a2e;
                        padding-top: 4px;
                        margin-top: 2px;
                    }
                    .total-row.grand-total .label {
                        font-size: 11.5pt;
                    }
                    .total-row.grand-total .value {
                        font-size: 11.5pt;
                    }
                    .total-row .value.text-success { color: #059669; }
                    .total-row .value.text-danger { color: #dc2626; }
                    .total-row .value.text-warning { color: #d97706; }
                    .total-row .value.text-primary { color: #2563eb; }
                    
                    /* ===== PAYMENT STATUS ===== */
                    .payment-status {
                        text-align: center;
                        padding: 3px 0;
                        margin: 3px 0;
                        font-size: 9.5pt;
                        font-weight: 700;
                        letter-spacing: 1.5px;
                        border-radius: 2px;
                    }
                    .payment-status.paid {
                        color: #059669;
                        background: #ecfdf5;
                    }
                    .payment-status.unpaid {
                        color: #dc2626;
                        background: #fef2f2;
                    }
                    .payment-status.partial {
                        color: #d97706;
                        background: #fffbeb;
                    }
                    
                    /* ===== PAYMENT METHOD ===== */
                    .payment-method {
                        display: flex;
                        justify-content: space-between;
                        font-size: 8pt;
                        padding: 2px 0;
                        color: #1a1a2e;
                    }
                    .payment-method .label {
                        font-weight: 600;
                    }
                    .payment-method .value {
                        font-weight: 500;
                        text-transform: uppercase;
                    }
                    
                    /* ===== FOOTER ===== */
                    .footer {
                        text-align: center;
                        padding-top: 5px;
                        border-top: 2px solid #1a1a2e;
                        margin-top: 5px;
                    }
                    .footer .thankyou {
                        font-size: 9pt;
                        font-weight: 700;
                        color: #1a1a2e;
                        letter-spacing: 1px;
                    }
                    .footer .tagline {
                        font-size: 7.5pt;
                        color: #4a4a5a;
                        margin-top: 1px;
                    }
                    .footer .powered {
                        font-size: 6.5pt;
                        color: #9a9aaa;
                        margin-top: 2px;
                        letter-spacing: 0.5px;
                    }
                    
                    /* ===== PRINT OPTIMIZATIONS ===== */
                    @media print {
                        body { padding: 2mm 2.5mm; }
                        .no-print { display: none; }
                    }
                    
                    @media print and (max-width: 80mm) {
                        body { padding: 1.5mm 2mm; }
                        .receipt { max-width: 100%; }
                        .shop-name { font-size: 13pt; }
                        .header-logo { max-height: 60px; max-width: 60px; }
                        .header { gap: 10px; padding: 3px 0 5px 0; }
                    }
                </style>
            </head>
            <body>
                <div class="receipt">
                    <!-- ===== HEADER ===== -->
                    <div class="header">
                        ${businessLogo ? `
                        <div class="header-logo-wrapper">
                            <img src="${businessLogo}" alt="${escapeHtml(shopName)}" class="header-logo" onerror="this.style.display='none'" />
                        </div>
                        ` : ''}
                        <div class="header-info">
                            <div class="shop-name">${escapeHtml(shopName)}</div>
                            ${shopAddress ? `<div class="shop-address">${escapeHtml(shopAddress)}</div>` : ''}
                            ${shopPhone ? `<div class="shop-contact">📞 <span class="phone">${escapeHtml(shopPhone)}</span></div>` : ''}
                        </div>
                    </div>

                    <!-- ===== BILL INFO ===== -->
                    <div class="bill-info">
                        <div class="bill-number">
                            <span class="label">BILL #</span> ${formData.bill_number}
                        </div>
                        <div class="bill-meta">
                            <span><span class="label">Date:</span> ${formatReceiptDate(formData.date)}</span>
                            <span><span class="label">Time:</span> ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                    </div>

                    <!-- ===== CUSTOMER INFO ===== -->
                    <div class="customer-info">
                        <span class="label">Customer:</span> ${escapeHtml(customerName)}
                        ${customerPhone ? ` | 📞 ${escapeHtml(customerPhone)}` : ''}
                        ${customerAddress ? ` | ${escapeHtml(customerAddress)}` : ''}
                    </div>

                    <!-- ===== ITEMS TABLE ===== -->
                    <table class="items-table">
                        <thead>
                            <tr>
                                <th class="col-item">Item</th>
                                <th class="col-qty">Qty</th>
                                <th class="col-price">Price</th>
                                <th class="col-total">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${formData.items.map((item) => {
                                const unitLabel = getUnitLabel(item);
                                const qtyDisplay = parseFloat(item.quantity).toFixed(0);
                                const priceDisplay = parseFloat(item.effective_sell_price || item.price).toFixed(2);
                                const totalDisplay = parseFloat(item.total || item.price * item.quantity).toFixed(2);
                                const itemName = escapeHtml(item.product_name || 'Product');
                                const discountDetail = getDiscountDetails(item);
                                
                                return `
                                <tr>
                                    <td class="col-item">
                                        <span class="item-name">${itemName}</span>
                                        ${discountDetail ? `<span class="item-discount-detail">${discountDetail}</span>` : ''}
                                    </td>
                                    <td class="col-qty">
                                        <span class="qty-with-unit">${qtyDisplay} <span class="unit">${escapeHtml(unitLabel)}</span></span>
                                    </td>
                                    <td class="col-price">${priceDisplay}</td>
                                    <td class="col-total">${totalDisplay}</td>
                                </tr>
                            `}).join('')}
                        </tbody>
                    </table>

                    <!-- ===== TOTALS ===== -->
                    <div class="totals">
                        <div class="total-row">
                            <span class="label">Subtotal</span>
                            <span class="value">${formatReceiptCurrency(formData.subtotal)}</span>
                        </div>
                        ${parseFloat(formData.tax) > 0 ? `
                        <div class="total-row">
                            <span class="label">Tax</span>
                            <span class="value">${formatReceiptCurrency(formData.tax)}</span>
                        </div>` : ''}
                        ${parseFloat(formData.discount) > 0 ? `
                        <div class="total-row">
                            <span class="label">Discount</span>
                            <span class="value text-danger">-${formatReceiptCurrency(formData.discount)}</span>
                        </div>` : ''}
                        ${hasAnyDiscount && totalDiscountAmount > 0 ? `
                        <div class="total-row" style="border-top:1px dashed #e0e0e8; padding-top:2px; margin-top:2px;">
                            <span class="label" style="color:#059669;">Total Discount</span>
                            <span class="value text-success">-${formatReceiptCurrency(totalDiscountAmount)}</span>
                        </div>` : ''}
                        <div class="total-row grand-total">
                            <span class="label">TOTAL</span>
                            <span class="value">${formatReceiptCurrency(formData.grand_total)}</span>
                        </div>
                        ${parseFloat(formData.paid_amount) > 0 ? `
                        <div class="total-row">
                            <span class="label">Paid</span>
                            <span class="value text-primary">${formatReceiptCurrency(formData.paid_amount)}</span>
                        </div>` : ''}
                        ${parseFloat(formData.balance) > 0 ? `
                        <div class="total-row">
                            <span class="label">Balance</span>
                            <span class="value text-warning">${formatReceiptCurrency(formData.balance)}</span>
                        </div>` : ''}
                    </div>

                    <!-- ===== PAYMENT STATUS ===== -->
                    ${parseFloat(formData.balance) <= 0 ? `
                    <div class="payment-status paid">● PAID</div>
                    ` : parseFloat(formData.paid_amount) > 0 ? `
                    <div class="payment-status partial">● PARTIAL</div>
                    ` : `
                    <div class="payment-status unpaid">● UNPAID</div>
                    `}

                    <!-- ===== PAYMENT METHOD ===== -->
                    ${formData.payment_method ? `
                    <div class="divider-light"></div>
                    <div class="payment-method">
                        <span class="label">Payment Method</span>
                        <span class="value">${escapeHtml(formData.payment_method)}</span>
                    </div>
                    ` : ''}

                    <!-- ===== FOOTER ===== -->
                    <div class="footer">
                        <div class="thankyou">✦ Thank You! For Visiting ✦</div>
                        <div class="powered">${translations?.poweredBy || 'Powered by OurZest (PVT) LTD'}</div>
                    </div>
                </div>

                <script>
                    window.onload = function() {
                        setTimeout(function() {
                            window.print();
                            window.onafterprint = function() { 
                                window.close(); 
                            };
                        }, 400);
                    };
                </script>
            </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();
    }
};

    // ============================================
    // handleSubmit
    // ============================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSavingRef.current) {
            showToast('warning', 'Please wait, sale is already being processed...');
            return;
        }

        isSavingRef.current = true;
        if (submitButtonRef.current) {
            submitButtonRef.current.disabled = true;
        }

        if (!currentShop?.shop_id) {
            showToast('error', translations?.noShopSelected || 'No shop selected. Please select a shop first.');
            isSavingRef.current = false;
            if (submitButtonRef.current) submitButtonRef.current.disabled = false;
            return;
        }

        if (!validateForm()) {
            isSavingRef.current = false;
            if (submitButtonRef.current) submitButtonRef.current.disabled = false;
            return;
        }

        setSaving(true);

        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) throw new Error("User not authenticated");

            const action = isEditMode ? 'edit' : 'add';
            
            const preparedItems = formData.items.map(item => {
                let finalQuantity = parseFloat(item.quantity);
                let sellMethod = item.sellMethod || 'packing';
                let conversionRate = item.items_per_pack || 1;

                return {
                    product_id: item.product_id,
                    product_name: item.product_name,
                    product_code: item.product_code || '',
                    batch_no: item.batch_no,
                    barcode: item.barcode,
                    unit: item.display_unit || item.unit_of_measure,
                    quantity: finalQuantity,
                    display_quantity: parseFloat(item.quantity),
                    sell_method: sellMethod,
                    packing_type: item.packing_type,
                    sub_packing_type: item.sub_packing_type,
                    unitorstrip: item.unitorstrip || '', // NEW: Include unitorstrip
                    items_per_pack: conversionRate,
                    unit_of_measure: item.unit_of_measure,
                    price: parseFloat(item.effective_sell_price || item.price),
                    total: parseFloat(item.effective_sell_price || item.price) * parseFloat(item.quantity),
                    color: item.color || '',
                    size: item.size || '',
                    expiry_date: item.expiry_date || null,
                    mrp: item.mrp || 0,
                    trade_percent: parseFloat(item.trade_percent) || 0,
                    extra_percent: parseFloat(item.extra_percent) || 0,
                    bonus_buy: parseInt(item.bonus_buy) || 0,
                    bonus_free: parseInt(item.bonus_free) || 0,
                    effective_sell_price: parseFloat(item.effective_sell_price) || 0,
                    effective_discount_percent: parseFloat(item.effective_discount_percent) || 0,
                    cost_price_per_pack: parseFloat(item.cost_price_per_pack) || 0,
                    cost_price_per_unit: parseFloat(item.cost_price_per_unit) || 0,
                    bonus_stock_quantity: parseFloat(item.bonus_stock_quantity) || 0,
                    actual_stock_quantity: parseFloat(item.actual_stock_quantity) || 0
                };
            });

            const payload = {
                action: action,
                username: currentUsername,
                shop_id: currentShop.shop_id,
                ...formData,
                items: preparedItems
            };
            if (isEditMode && saleId) payload.id = saleId;

            const response = await fetch(SELL_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', isEditMode ? (translations?.saleUpdated || 'Sale updated successfully!') : (translations?.saleCompleted || 'Sale completed successfully!'));

                if (!isEditMode) {
                    setFormData({
                        ...initialFormData,
                        date: new Date().toISOString().split('T')[0],
                        bill_number: data.data?.bill_number || '',
                        bill_number_int: data.data?.bill_number_int || 0
                    });
                    setProductSearchTerm('');
                    setErrors({});
                    getNextBillNumber();

                    setTimeout(() => {
                        if (customerNameRef.current) {
                            customerNameRef.current.focus();
                        }
                    }, 100);
                }

                if (onSuccess) onSuccess(data.data?.sell_id || saleId);

                setTimeout(() => {
                    if (data.data) {
                        setFormData(prev => ({
                            ...prev,
                            bill_number: data.data.bill_number || prev.bill_number,
                            date: data.data.date || prev.date,
                            subtotal: data.data.subtotal || prev.subtotal,
                            tax: data.data.tax || prev.tax,
                            discount: data.data.discount || prev.discount,
                            grand_total: data.data.grand_total || prev.grand_total,
                            paid_amount: data.data.paid_amount || prev.paid_amount,
                            balance: data.data.balance || prev.balance,
                            payment_method: data.data.payment_method || prev.payment_method,
                            customer_name: data.data.customer_name || prev.customer_name,
                            customer_phone: data.data.customer_phone || prev.customer_phone,
                            customer_address: data.data.customer_address || prev.customer_address
                        }));
                        setTimeout(handleDirectPrint, 500);
                    } else {
                        handleDirectPrint();
                    }
                }, 300);
            } else {
                showToast('error', data.message || (isEditMode ? translations?.failedUpdate : translations?.failedSave) || 'Failed to save sale');
            }
        } catch (error) {
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            isSavingRef.current = false;
            setSaving(false);
            setTimeout(() => {
                if (submitButtonRef.current) submitButtonRef.current.disabled = false;
            }, 2000);
        }
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{isEditMode ? (translations?.editSale || 'Edit Sale') : (translations?.newSale || 'New Sale')}</h1>
                            <p>{translations?.sellProducts || 'Sell Products'}</p>
                        </div>
                    </div>

                    <div className="ozui-header-right">
                        <div className="ozui-header-actions">
                            <button
                                className="ozui-action-btn clear-draft"
                                onClick={clearDraft}
                                disabled={saving}
                                title={translations?.clearForm || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>{translations?.clear || 'Clear'}</span>
                            </button>

                            <button
                                className="ozui-action-btn save-draft"
                                onClick={saveDraft}
                                disabled={saving}
                                title={translations?.saveDraft || 'Save as Draft'}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                                <span>{translations?.saveDraft || 'Save Draft'}</span>
                            </button>

                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button
                                    className="ozui-action-btn drafts-btn"
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title={translations?.viewDrafts || 'View Drafts'}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{translations?.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>

                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations?.savedDrafts || 'Saved Drafts'}</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations?.noDraftsSaved || 'No drafts saved'}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">
                                                                {draft.bill_number || `Sale #${draft.id}`}
                                                            </div>
                                                            <div className="draft-details">
                                                                {draft.customer_name && <span>{draft.customer_name}</span>}
                                                                {draft.items && <span>{draft.items.length} items</span>}
                                                            </div>
                                                            <div className="draft-date">
                                                                {new Date(draft.savedAt).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="delete-draft-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteDraft(draft.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                {/* Barcode Scanner Status Bar */}
                <div style={{
                    background: isScannerActive ? '#f0fdf4' : '#f9fafb',
                    border: `1px solid ${isScannerActive ? '#bbf7d0' : '#e5e7eb'}`,
                    borderRadius: '6px',
                    padding: '6px 12px',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '0.85rem'
                }}>
                    <FontAwesomeIcon icon={faBarcode} style={{ color: isScannerActive ? '#10b981' : '#9ca3af' }} />
                    <span style={{ flex: 1, color: isScannerActive ? '#059669' : '#6b7280', fontWeight: 500 }}>
                        {isScannerActive ? (translations?.scannerReady || 'Scanner Ready') : 'Scanner Paused'}
                    </span>
                    <button
                        type="button"
                        onClick={() => setIsScannerActive(!isScannerActive)}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: isScannerActive ? '#059669' : '#6b7280',
                            fontSize: '1.1rem',
                            padding: '2px 6px'
                        }}
                    >
                        <FontAwesomeIcon icon={isScannerActive ? faToggleOn : faToggleOff} />
                    </button>
                    {lastScannedBarcode && (
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            Last: {lastScannedBarcode}
                        </span>
                    )}
                </div>

                {/* Hidden Barcode Input */}
                <input
                    ref={barcodeInputRef}
                    type="text"
                    style={{ position: 'absolute', left: '-9999px', opacity: 0, width: '1px', height: '1px' }}
                    autoComplete="off"
                    tabIndex={-1}
                    onChange={() => {}}
                />

                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                id="bill_number"
                                value={formData.bill_number}
                                readOnly
                                placeholder=" "
                                className="ozui-readonly-input"
                            />
                            <label htmlFor="bill_number">
                                <FontAwesomeIcon icon={faFileInvoice} />
                                {translations?.billNumber || 'Bill Number'}
                            </label>
                        </div>
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="date"
                                id="date"
                                value={formData.date}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                            />
                            <label htmlFor="date">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {translations?.date || 'Date'}
                            </label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field" ref={customerSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input
                                ref={customerNameRef}
                                type="text"
                                name="customer_name"
                                id="customer_name"
                                value={formData.customer_name}
                                onChange={handleChange}
                                onFocus={() => formData.customer_name.length >= 2 && setShowCustomerDropdown(true)}
                                placeholder=" "
                                disabled={saving}
                                autoComplete="off"
                            />
                            <label htmlFor="customer_name">
                                <FontAwesomeIcon icon={faUser} />
                                {translations?.customerName || 'Customer Name'}
                                <span>({translations?.optional || 'Optional'})</span>
                            </label>
                            {searchingCustomer && <FontAwesomeIcon icon={faSpinner} spin className="ozui-search-indicator" />}
                        </div>
                        {showCustomerDropdown && customerSearchResults.length > 0 && (
                            <div className="ozui-customer-dropdown">
                                {customerSearchResults.map(customer => (
                                    <div key={customer.customerid} className="ozui-dropdown-item" onClick={() => selectCustomer(customer)}>
                                        <div className="ozui-customer-name">{customer.customer_name}</div>
                                        {customer.phone && <div className="ozui-customer-phone"><FontAwesomeIcon icon={faPhone} /> {customer.phone}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="tel"
                                name="customer_phone"
                                id="customer_phone"
                                value={formData.customer_phone}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                            />
                            <label htmlFor="customer_phone">
                                <FontAwesomeIcon icon={faPhone} />
                                {translations?.customerPhone || 'Phone Number'}
                                <span>({translations?.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Product Search with Client-side filtering */}
                <div className="ozui-form-row">
                    <div className="ozui-field" ref={productSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                id="product_search"
                                value={productSearchTerm}
                                onChange={(e) => handleProductSearch(e.target.value)}
                                onFocus={() => {
                                    if (productSearchTerm && productSearchTerm.length >= 1) {
                                        handleProductSearch(productSearchTerm);
                                    }
                                }}
                                placeholder=" "
                                disabled={saving}
                                autoComplete="off"
                            />
                            <label htmlFor="product_search">
                                <FontAwesomeIcon icon={faSearch} />
                                {translations?.searchProducts || 'Search Products'}
                            </label>
                            {loadingProducts && <FontAwesomeIcon icon={faSpinner} spin className="ozui-search-indicator" />}
                        </div>
                        {showProductDropdown && filteredProducts.length > 0 && (
                            <div className="ozui-product-dropdown">
                                {filteredProducts.map(product => (
                                    <div key={product.id} className="ozui-product-item" onClick={() => handleProductSelect(product)}>
                                        <div className="ozui-product-item-name">
                                            {product.product_name}
                                            {product.product_code && <span className="ozui-product-code-badge">{product.product_code}</span>}
                                            {product.mrp > 0 && <span className="ozui-product-mrp-badge">MRP: {product.mrp}</span>}
                                        </div>
                                        <div className="ozui-product-item-details">
                                            {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>}
                                            {product.category && <span><FontAwesomeIcon icon={faBox} /> {product.category}</span>}
                                            {product.company && <span><FontAwesomeIcon icon={faBuilding} /> {product.company}</span>}
                                            {product.pack_content_label && <span style={{ fontSize: '0.6rem', color: '#6b7280' }}>{product.pack_content_label}</span>}
                                        </div>
                                        <div className="ozui-product-item-price-stock">
                                            <span className="ozui-product-price">
                                                <FontAwesomeIcon icon={faRupeeSign} /> 
                                                {parseFloat(product.mrp || product.effective_sell_price || product.sell_per_pack || 0).toFixed(2)}
                                            </span>
                                            <span className={`ozui-product-stock ${parseFloat(product.total_quantity) <= (product.quantity_alert || 5) ? 'ozui-low-stock' : ''}`}>
                                                Stock: {product.total_quantity} packs / {parseFloat(product.available_bulk || 0).toFixed(2)} {product.unit_of_measure || 'units'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {showProductDropdown && filteredProducts.length === 0 && productSearchTerm.length >= 1 && (
                            <div className="ozui-product-dropdown" style={{ padding: '10px', textAlign: 'center', color: '#6b7280' }}>
                                No products found. Try a different search term.
                            </div>
                        )}
                    </div>
                </div>

                {formData.items.length > 0 && (
                    <ItemsTable
                        items={formData.items}
                        onItemChange={handleItemChange}
                        onRemove={removeItem}
                        onAdd={addItem}
                        onSellMethodChange={handleSellMethodChange}
                        onDiscountChange={handleDiscountChange}
                        errors={errors}
                        disabled={saving}
                        translations={translations}
                        isRTL={isRTL}
                    />
                )}

                {formData.items.length > 0 && (
                    <div className="ozui-form-section">
                        <div className="ozui-calculations">
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.subtotal || 'Subtotal'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.subtotal}</span>
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.tax || 'Tax'}:</span>
                                <div className="ozui-input-wrapper ozui-small-input">
                                    <input type="number" name="tax" value={formData.tax} onChange={handleChange} step="0.01" min="0" placeholder="0.00" disabled={saving} />
                                </div>
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.discount || 'Discount'}:</span>
                                <div className="ozui-input-wrapper ozui-small-input">
                                    <input type="number" name="discount" value={formData.discount} onChange={handleChange} step="0.01" min="0" placeholder="0.00" disabled={saving} />
                                </div>
                            </div>
                            <div className="ozui-calculation-row ozui-grand-total">
                                <span className="ozui-calculation-label">{translations?.grandTotal || 'Grand Total'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.grand_total}</span>
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.paidAmount || 'Paid Amount'}:</span>
                                <div className="ozui-input-wrapper ozui-small-input">
                                    <input type="number" name="paid_amount" value={formData.paid_amount} onChange={handlePaidAmountChange} step="0.01" min="0" max={formData.grand_total} placeholder="0.00" disabled={saving} />
                                </div>
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.balance || 'Balance'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.balance}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="ozui-form-actions">
                    {formData.items.length > 0 && (
                        <button
                            type="button"
                            className="ozserui-action-btn"
                            onClick={handleDirectPrint}
                            title={translations?.printReceipt || 'Print Receipt'}
                            style={{ background: 'var(--ozserui-primary)', color: 'white', padding: '0.5rem 1rem' }}
                        >
                            <FontAwesomeIcon icon={faReceipt} />
                            <span>{translations?.receipt || 'Receipt'}</span>
                        </button>
                    )}

                    <button
                        ref={submitButtonRef}
                        type="submit"
                        className="ozui-btn ozui-btn-primary"
                        disabled={saving || formData.items.length === 0}
                    >
                        {saving ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{isEditMode ? (translations?.updateSale || 'Update Sale') : (translations?.completeSale || 'Complete Sale')}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>

            {loading && (
                <div className="ozui-loading-overlay">
                    <div className="ozui-loading-content">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        <p>{translations?.loading || 'Loading...'}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PharmacySell;